import React, {
    useCallback, useEffect, useMemo, useContext
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { readViewModelValue } from '@xengage/gw-jutro-adapters-react';
import { useValidation } from '@xengage/gw-portals-validation-react'
import { ViewModelServiceContext, ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import classNames from 'classnames';
import { wizardProps } from '@xengage/gw-portals-wizard-react';
import { withRouter } from 'react-router-dom';
import { Link } from '@jutro/router';
import { InputField, InputNumberField, Button, Icon } from '@jutro/legacy/components';
import { accessibilityAddTableRow, focusNextElement, screenReaderAnnounce } from 'premier-capability-hooks';
import styles from './TenderDinghyBoatsComponent.module.scss';
import metadata from './TenderDinghyBoatsComponent.metadata.json5';
import messages from './TenderDinghyBoatsComponent.messages';
import { TranslatorContext } from '@jutro/legacy/locale';

function TenderDinghyBoatsComponent(props) {

    const {
        data: TenderDinghyBoatsVM,
        updateWizardData,
        submissionVM,
        TenderDinghyBoatsPath,
        boatInformationPath,
        id: tenderDinghyBoatsId
        
    } = props;
    const translator = useContext(TranslatorContext);
    const viewModelService = useContext(ViewModelServiceContext);

    const addTenderDinghyBoats = useCallback(
        (event) => {
            if (event) {
                event.preventDefault();
            }

            TenderDinghyBoatsVM.value = TenderDinghyBoatsVM.value || []

            const { _xCenter, _dtoName } = TenderDinghyBoatsVM;

            const TenderDinghyBoatsObjVM = viewModelService.create(
                {},
                _xCenter,
                _dtoName
            );

            TenderDinghyBoatsObjVM.isValidationReq = true;
            TenderDinghyBoatsObjVM.yeaFie = (_.get(submissionVM, boatInformationPath))?.value?.yeaFie;

            TenderDinghyBoatsVM.pushElement(
                TenderDinghyBoatsObjVM
            );
            updateWizardData(submissionVM);

            accessibilityAddTableRow('Tender Dinghy Boats');
            setTimeout(() => {
                focusNextElement(true,0, `${tenderDinghyBoatsId}yeaFie`);
            }, 3000);            
        },
        [TenderDinghyBoatsVM, viewModelService, submissionVM, boatInformationPath, updateWizardData]
    );

    const handleValueChange = useCallback(
        () => {
            updateWizardData(submissionVM);
        },
        [submissionVM, updateWizardData]
    );

    const removeAssignmentItem = useCallback(
        (event, index) => {
            if (event) {
                event.preventDefault();
            }

            const removedTenderDinghyBoat = TenderDinghyBoatsVM.value[index];

            TenderDinghyBoatsVM.value.splice(index, 1);
            _.set(submissionVM.value, TenderDinghyBoatsPath, TenderDinghyBoatsVM.value);
            updateWizardData(submissionVM);
            screenReaderAnnounce(translator(messages.removeTenderDinghyBoat, {brandDesc: removedTenderDinghyBoat.braFie}))
            focusNextElement(true, 0, `addTenderDinghyBoatsButtonId`); 
        }, [submissionVM, updateWizardData, TenderDinghyBoatsPath, TenderDinghyBoatsVM?.value]
    );

    const getTableCell = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-shadow
        (item, index, props) => {
            const { path } = props;

            if (path === 'numFie') {
                const tempTenderDinghyBoatsPath = 'lobData.recMarLine.coverables.boats[0].additionalParts.tenderDinghyBoatsTable'
                const auxiliaryArray = _.get(submissionVM.value, tempTenderDinghyBoatsPath);
                const newNumber = auxiliaryArray?.length >= 2 ? auxiliaryArray[auxiliaryArray.length - 2].numFie + 1 : index + 1;

                if (auxiliaryArray[index].numFie === undefined) {
                    auxiliaryArray[index].numFie = newNumber;
                    _.set(submissionVM.value, tempTenderDinghyBoatsPath, TenderDinghyBoatsVM.value);
                }

                const value = item.numFie ? item.numFie : newNumber;

                return (
                    <InputField
                        id={index + path}
                        className={styles.tableColumn}
                        path={path}
                        dataType="number"
                        value={value}
                        readOnly
                    />
                );
            }

            if (path === 'yeaFie') {
                const value = item.yeaFie ? item.yeaFie : (_.get(submissionVM, boatInformationPath))?.value?.yeaFie;

                return (
                    <InputField
                        id={index + tenderDinghyBoatsId + path}
                        className={styles.tableColumn}
                        path={path}
                        dataType="String"
                        onValueChange={(val, changedPath) => {
                            const filteredVal = String(val).replace(/[^0-9]/g, ''); // keep only digits

                            item.yeaFie = filteredVal;
                            handleValueChange(filteredVal, changedPath, index);
                        }}
                        value={value}
                        maxLength="4"
                    />
                );
            }

            if (path === 'braFie') {
                const value = item.braFie ? item.braFie : '';

                return (
                    <InputField
                        id={index + path}
                        className={styles.tableColumn}
                        path={path}
                        dataType="String"
                        onValueChange={(val, changedPath) => {
                            item.braFie = val;
                            handleValueChange(val, changedPath, index);
                        }}
                        value={value}
                    />
                );
            }

            if (path === 'lenFie') {
                const value = item.lenFie ? item.lenFie : '';

                return (
                    <InputNumberField
                        id={index + path}
                        className={styles.tableColumn}
                        path={path}
                        dataType="number"
                        onValueChange={(val, changedPath) => {
                            item.lenFie = val;
                            handleValueChange(val, changedPath, index);
                        }}
                        value={value}
                    />
                );
            }

            if (path === 'serFie') {
                const value = item.serFie ? item.serFie : '';

                return (
                    <InputField
                        id={index + path}
                        className={styles.tableColumn}
                        path={path}
                        dataType="number"
                        onValueChange={(val, changedPath) => {
                            const filteredVal = String(val).replace(/[^0-9]/g, ''); // keep only digits

                            item.serFie = filteredVal;
                            handleValueChange(filteredVal, changedPath, index);
                        }}
                        value={value}
                    />
                );
            }

            if (path === 'valFie') {
                const value = item.valFie ? item.valFie : '';

                return (
                    <InputField
                        id={index + path}
                        className={styles.tableColumn}
                        path={path}
                        dataType="number"
                        onValueChange={(val, changedPath) => {
                            const sanitized = String(val)
                                .replace(/[^0-9.]/g, '') // remove all but digits and dots
                                .replace(/^([^.]*\.)|\./g, '$1'); // allow only one dot

                            item.valFie = sanitized;
                            handleValueChange(sanitized, changedPath, index);
                        }}
                        value={value}
                    />
                );
            }

        }, [submissionVM.value, TenderDinghyBoatsVM.value, handleValueChange])

    const removeAssignmentTableRow = useCallback(
        (item, index) => (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            (<Link
                onClick={(e) => removeAssignmentItem(e, index, item)}
                className={classNames(styles.deleteSection, styles.deleteRow)}
            >
                <Icon icon="mi-delete" className={styles.deleteIcon} />
            </Link>)
        ),
        [removeAssignmentItem, translator]
    );

     const renderAddButton = useCallback(() => (
        <Button type='button' id='addTenderDinghyBoatsButtonId' aria-label ={translator(messages.addTenderDinghyBoat)} onClick={(e) => addTenderDinghyBoats(e)}>
                {translator(messages.add)}
            </Button>
    ), [addTenderDinghyBoats, translator]);

    const overrideProps = useMemo(() => ({
        '@field': {
            showOptional: false,
            showRequired: true
        },
        [`TenderDinghyBoatsTable`]: {
            data: TenderDinghyBoatsVM.value,
        },
        'addTenderDinghyBoatsbtnDiv': {
            content: renderAddButton()
        }

    }), [TenderDinghyBoatsVM.value, renderAddButton])

    const resolvers = {
        resolveCallbackMap: {
            addTenderDinghyBoats,
            getTableCell,
            removeAssignmentTableRow
        }
    };

    const readValue = useCallback(
        (fieldId, fieldPath) => readViewModelValue(
            metadata.pageContent,
            TenderDinghyBoatsVM,
            fieldId,
            fieldPath,
            overrideProps
        ),
        [TenderDinghyBoatsVM, overrideProps]
    );

    return (
        <ViewModelForm
            uiProps={metadata.pageContent}
            model={TenderDinghyBoatsVM}
            overrideProps={overrideProps}
            resolveValue={readValue}
            onValueChange={handleValueChange}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );


}

TenderDinghyBoatsComponent.propTypes = wizardProps;
export default withRouter(TenderDinghyBoatsComponent);
